<template>
  <div class="page-wrapper">
    <div v-if="loading" class="loader-wrapper">
      <spinner />
    </div>
    <div v-else-if="view === 'form'" class="form-wrapper">
      <div class="form-card">
        <div class="content">
          <div class="left">
            <h1 class="page-title">{{ $t('membershipRequest') }}</h1>
            <div class="plan-select-wrapper">
              <p class="section-label">{{ $t('chooseMembershipType') }}</p>
              <v-radio-group v-model="selectedPlanId">
                <v-radio
                  v-for="plan in plans"
                  :key="plan.id"
                  class="plan-radio"
                  :value="plan.id"
                  :label="plan.name"
                  color="#0277BD"
                />
              </v-radio-group>
            </div>
            <hr class="divider" style="margin-bottom: 1.5rem">

            <accreditation-form
              v-if="schema.attendeeFields && schema.attendeeFields.length"
              :event="fakeEvent"
              :styles="{ form: {} }"
              :fields="schema.attendeeFields"
              :initial-values="form"
              :vs="$v"
              @values="form = $event"
            />
            <div class="submit-btn-wrapper">
              <main-button depressed @click="handleSubmit">
                {{ $t('sendRequest') }} <v-icon>mdi-arrow-right</v-icon>
              </main-button>
            </div>
          </div>

          <div v-if="organization" class="right">
            <div class="org-card">
              <img
                v-if="organization.imageUrls && organization.imageUrls.length"
                :alt="adminText('organizationAll.organization_cover_26982813', 'organization cover')"
                :src="organization.imageUrls[0]"
              >
              <div class="org-name">{{ organization.publicName || organization.name }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <membership-request-result
      v-else-if="['error', 'success'].includes(view)"
      :status="view"
      @primaryClick="
        view = 'form'
        resetForm()
      "
    />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import Spinner from '@/components/shared/spinner.vue'
import AccreditationForm from '@/components/accreditations/accreditation-form.vue'
import { GET_SPORT_ORGANIZATION_MEMBERSHIP_PLANS } from '@/graphql/membershipPlan.graphql'
import { GET_ORGANIZATION_BY_SLUG } from '@/graphql/organization.graphql'
import { createAttendeeSchema } from '@/utils'
import { CREATE_PUBLIC_SPORT_MEMBERSHIP_REQUEST } from '@/graphql/membershipUser.graphql'
import MembershipRequestResult from '@/components/organizations/memberships/MembershipRequestResult.vue'
import i18next from 'i18next'
import moment from 'moment'

export default {
  mixins: [OrganizationLocalization],
  name: 'membership-web-registration',
  components: {
    Spinner,
    AccreditationForm,
    MembershipRequestResult,
  },
  data() {
    return {
      loading: true,
      organizationSlug: this.$route.params.organizationSlug,
      plans: [],
      selectedPlanId: null,
      schema: {},
      form: {},
      organization: null,
      view: 'form', // form | success | error
      attendeeStyles: {
        title: '  ',
        titleDescription: 'Obvezna su polja označena zvjezdicom.',
        submit: 'POŠALJI ZAHTJEV',
      },
    }
  },
  validations() {
    return {
      form: {
        ...this.schema.validationSchema,
      },
    }
  },
  computed: {
    membershipPlanIdFromQuery() {
      return this.$route.query.membershipPlanId
    },
    languageFromParams() {
      return this.$route.query.lang || 'hr'
    },
    fakeEvent() {
      // minimal object AccreditationForm očekuje – ne koristimo ticketing/workshops featurse
      return {
        id: 0,
        extraModules: {
          categoriesAndZones: false,
          expoMobileApp: false,
        },
      }
    },
  },
  async created() {
    await this.fetchConfig()
  },
  methods: {
    async fetchConfig() {
      try {
        const [{ data: plansData }, { data: orgData }] = await Promise.all([
          this.$apollo.query({
            query: GET_SPORT_ORGANIZATION_MEMBERSHIP_PLANS,
            variables: {
              organizationSlug: this.organizationSlug,
            },
          }),
          this.$apollo.query({
            query: GET_ORGANIZATION_BY_SLUG,
            variables: {
              organizationSlug: this.organizationSlug,
            },
          }),
        ])

        this.plans = (plansData.getOrganizationMembershipPlans || []).filter(p =>
          moment(p.to).isAfter(moment()),
        )
        this.organization = orgData.getOrganizationBySlug || null

        if (this.membershipPlanIdFromQuery) {
          const preselected = this.plans.find(
            p => String(p.id) === String(this.membershipPlanIdFromQuery),
          )
          const language = this.languageFromParams
          await i18next.changeLanguage(language)

          if (preselected) {
            this.selectedPlanId = preselected.id
          }
        }

        // Schema je definirana u adminu kao organization.attendeeFields.membershipFields
        const attendeeFields =
          this.organization &&
          this.organization.attendeeFields &&
          this.organization.attendeeFields.membershipFields
            ? this.organization.attendeeFields.membershipFields
            : {}
        this.schema = createAttendeeSchema(attendeeFields)
      } catch (e) {
        this.view = 'error'
      } finally {
        this.loading = false
      }
    },
    async handleSubmit() {
      if (!this.selectedPlanId) {
        return
      }
      try {
        const dto = {
          membershipPlanId: Number(this.selectedPlanId),
          fields: this.form,
        }
        const { errors } = await this.$apollo.mutate({
          mutation: CREATE_PUBLIC_SPORT_MEMBERSHIP_REQUEST,
          variables: { dto },
        })
        if (errors) {
          this.view = 'error'
        } else {
          this.view = 'success'
        }
      } catch (e) {
        this.view = 'error'
      }
    },
    resetForm() {
      this.form = {}
      this.view = 'form'
    },
  },
}
</script>

<style scoped>
.page-wrapper {
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.loader-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-wrapper,
.result-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-card,
.result-card {
  width: 100%;
  background: #ffffff;
  padding: 2.5rem 5%;
}

.header {
  margin-bottom: 1.5rem;
}

.logo {
  height: 24px;
  margin-bottom: 1.5rem;
}

.page-title {
  color: var(--Grey-00, #333);
  font-variant-numeric: lining-nums proportional-nums;

  /* Heading/H3 */
  font-family: Raleway;
  font-size: 36px;
  font-style: normal;
  font-weight: 800;
  line-height: 120%; /* 43.2px */
  letter-spacing: -1.08px;
}

.title {
  margin: 0;
  font-family: 'Raleway';
  font-weight: 700;
  font-size: 1.75rem;
}

.content {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  justify-content: center;
}

@media (max-width: 1000px) {
  .content {
    flex-direction: column-reverse;
  }
}

.left {
  flex: 0 0 auto;
  max-width: 803px;
  width: 100%;
}

.right {
  flex: 1;
  max-width: 360px;
}

.plan-radio {
  display: flex;
  padding: 16px 24px 16px 16px;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid var(--Stroke-One, rgba(151, 151, 151, 0.5));
  background: var(--Grey-White, #fff);
}
.section-label {
  color: var(--Grey-00, #333);
  font-variant-numeric: lining-nums proportional-nums;

  /* Heading/H4 */
  font-family: Raleway;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%; /* 28.8px */
  letter-spacing: -0.72px;
}

.required-hint {
  margin: 1.5rem 0 1rem;
  font-size: 0.875rem;
  color: #666666;
}

.org-card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
}

.org-card img {
  width: 100%;
  display: block;
  object-fit: cover;
}

.org-name {
  padding: 0.75rem 1rem 1rem;
  font-weight: 700;
}

.result-wrapper.success .result-icon {
  width: 72px;
}

.result-wrapper.error .result-icon {
  width: 72px;
}

.result-card {
  text-align: left;
  max-width: 600px;
}

.result-title {
  margin: 1.5rem 0 0.75rem;
  font-family: 'Raleway';
  font-weight: 700;
  font-size: 1.75rem;
}

.result-text {
  margin-bottom: 2rem;
  font-size: 1rem;
  color: #555;
}

.footer {
  padding: 1.5rem 5%;
  border-top: 1px solid #e0e0e0;
  text-align: left;
  color: #999;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .content {
    grid-template-columns: 1fr;
  }

  .form-card,
  .result-card {
    padding: 1.5rem;
  }
}

.submit-btn-wrapper {
  display: flex;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
  background: var(--dinamo-opacity-10, rgba(0, 130, 255, 0.1));
}
</style>
